import { ThemeSource } from '@app/lib/helpers/generate-theme';
import { safeJsonParse } from '@helpers/utils';
import {
    extractDetailLabels,
    extractFeatureDescriptions,
    extractFeatureIcons,
    extractProductGender,
    extractShopifyTemperature,
} from '../utilities';
import { ShopifyTemperature } from './normalise-fabric-temperature';

export type VideoSyncItem = {
    id: string;
    filename: string;
};

export type VideoSyncList = VideoSyncItem[] | null;

export interface ProcessedProductMetafields {
    gender: string;
    shortDescription: string;
    longDescription: string;
    fallbackProductFeatures: string;
    detailLabels: any[];
    featureDescriptions: string[];
    featureIcons: string[];
    backgroundColor: string;
    componentTheme: ThemeSource | undefined;
    shopifyTemperature: ShopifyTemperature | null;
    shopifyCareInstructions: string;
    shopifyRidingConditions: string;
    shopifyProductWeight: string;
    shopifyFabricContent: string;
    shopifyProductSizing: string;
    parentSKU: string;
    prismicDocumentUIDS: string[];
    prismicFeatureDocumentUID: string | null;
    pdpFeatureImagesPrismicDocumentUID: string | null;
    mediaGalleryVideo: string;
    featureVideo: string;
    featureVideoLocation: string;
    fit: string;
    pdpGalleryVideosSynced: VideoSyncList;
    pdpFeatureVideosSynced: VideoSyncList;
}

export function processProductMetafields(product): ProcessedProductMetafields {
    if (!product || !product.metafields)
        return {} as ProcessedProductMetafields;

    const metafieldsMap = product.metafields.reduce((acc, field) => {
        if (field && field.namespace && field.key) {
            acc[`${field.namespace}.${field.key}`] = field.value;
        }
        return acc;
    }, {});

    return {
        gender: extractProductGender(metafieldsMap),
        shortDescription:
            metafieldsMap['product.short_description'] || product.description,
        longDescription:
            metafieldsMap['custom.long_form_pdp_description'] ||
            'Description temporarily unavailable.',
        fallbackProductFeatures: metafieldsMap['product.long_description'],
        detailLabels: extractDetailLabels(metafieldsMap),
        featureDescriptions: extractFeatureDescriptions(metafieldsMap),
        featureIcons: extractFeatureIcons(metafieldsMap),
        backgroundColor: metafieldsMap['custom.feature_block_hex_code'],
        componentTheme:
            metafieldsMap[
                'custom.light_text_or_dark_text_linked_to_hex_code_value_field'
            ],
        shopifyTemperature: extractShopifyTemperature(metafieldsMap),
        shopifyCareInstructions: metafieldsMap['custom.care_instructions'],
        shopifyRidingConditions: metafieldsMap['custom.conditions'],
        shopifyProductWeight: metafieldsMap['custom.product_weight'],
        shopifyFabricContent: metafieldsMap['custom.material_composition'],
        shopifyProductSizing: metafieldsMap['product.sizing'],
        parentSKU: product.parentSKU?.value,
        prismicDocumentUIDS:
            metafieldsMap['custom.prismic_document_uids']?.split(','),
        prismicFeatureDocumentUID:
            metafieldsMap['custom.prismic_feature_document_uid'] ?? null,
        pdpFeatureImagesPrismicDocumentUID:
            metafieldsMap['custom.pdp_feature_images_prismic_document_uid'] ??
            null,
        mediaGalleryVideo: metafieldsMap['custom.pdp_media_gallery_video'],
        featureVideo: metafieldsMap['custom.pdp_feature_component_video'],
        featureVideoLocation:
            metafieldsMap['custom.pdp_feature_component_video_location'],
        fit: metafieldsMap['custom.maap_fit'],
        pdpGalleryVideosSynced: safeJsonParse(
            metafieldsMap['custom.product_gallery_videos_synced']
        ) as VideoSyncList,
        pdpFeatureVideosSynced: safeJsonParse(
            metafieldsMap['custom.product_feature_videos_synced']
        ) as VideoSyncList,
    };
}
